import React, { useState } from 'react';
import {
  useCreateShopifyStoreMutation,
  useUpdateShopifyStoreMutation,
  GetShopifyStoreBySceneDocument,
  ShopifyStore,
} from '@src/components/generated/graphql';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { SHOP_PRODUCTS_QUERY } from '../../side-bar-menu/shopify/queries';
import { LoadingOutlined } from '@ant-design/icons';
import { openNotification } from 'util/helpers';
import { Form, Input, Button, Spin } from 'antd';
import styles from './shopify-products.module.css';

const notify = (type, msg, desc) => {
  openNotification(msg, desc, 'top', 2, type);
};

type Props = {
  sceneId: string;
  store?: ShopifyStore | undefined;
  closeSettings?: Function | undefined;
};

const ShopifySettings: React.FC<Props> = ({
  sceneId,
  store,
  closeSettings,
}) => {
  const [loading, setLoading] = useState(false);
  const [createStore] = useCreateShopifyStoreMutation();
  const [updateStore] = useUpdateShopifyStoreMutation();

  const error = () =>
    notify(
      'error',
      'Error',
      'Please check your data in form and try again or contact ozone support'
    );

  async function onFinish(values) {
    setLoading(true);
    try {
      const client = new ApolloClient({
        uri: `https://${values.domain}/api/${values.apiVersion}/graphql.json`,
        headers: {
          'X-Shopify-Storefront-Access-Token': values.token as string,
        },
        cache: new InMemoryCache(),
      });
      const { data } = await client.query({
        query: SHOP_PRODUCTS_QUERY,
      });

      if (!data) {
        error();
        setLoading(false);
        return;
      }
    } catch (e) {
      console.error(e);
      error();
      setLoading(false);
      return;
    }
    let res;

    if (store) {
      res = await updateStore({
        variables: {
          data: {
            id: store.id,
            token: values.token,
            domain: values.domain,
            apiVersion: values.apiVersion,
          },
        },
        refetchQueries: [GetShopifyStoreBySceneDocument],
      });
    } else {
      res = await createStore({
        variables: {
          where: {
            sceneId,
            token: values.token,
            domain: values.domain,
            apiVersion: values.apiVersion,
          },
        },
        refetchQueries: [GetShopifyStoreBySceneDocument],
      });
    }

    if (!res) {
      error();
      setLoading(false);
      return;
    }

    if (store && closeSettings) {
      closeSettings();
    }

    notify('success', 'Success', 'Store added successfully');
    setLoading(false);
  }

  return (
    <div>
      <div className={styles.configRoot}>
        {!store && <h3 className={styles.title}>Configure Shopify store</h3>}
        <Form layout='vertical' onFinish={onFinish} style={{ marginTop: 40 }}>
          <Form.Item
            label='Store Domain'
            name='domain'
            initialValue={store && store.domain}
            rules={[{ required: true, message: 'Please enter Store Domain' }]}
          >
            <Input placeholder='Enter domain'></Input>
          </Form.Item>
          <Form.Item
            label='Store Front API token'
            name='token'
            initialValue={store && store.token}
            rules={[
              { required: true, message: 'Please enter Store Front API Token' },
            ]}
          >
            <Input placeholder='Enter token'></Input>
          </Form.Item>
          <Form.Item
            label='API version'
            name='apiVersion'
            initialValue={store && store.apiVersion}
            rules={[{ required: true, message: 'Please enter API version' }]}
          >
            <Input placeholder='Example: 2023-04'></Input>
          </Form.Item>
          <div className={styles.btnWrapper}>
            <Button
              htmlType='submit'
              type='primary'
              className={styles.btn}
              disabled={loading}
            >
              {loading ? (
                <Spin indicator={<LoadingOutlined spin />}></Spin>
              ) : store ? (
                'Update'
              ) : (
                'Add store'
              )}
            </Button>
            {store && (
              <Button type='dashed' onClick={closeSettings as any}>
                Cancel
              </Button>
            )}
          </div>
        </Form>
        {!store && (
          <>
            <p style={{ marginTop: 45 }}>Instructions:</p>
            <p>
              1. Install development app in your shopify store admin page.
              <br />
              <a
                className={styles.guide}
                target='_blank'
                href='https://help.shopify.com/en/manual/apps/app-types/custom-apps?shpxid=ddb55a21-CBD3-4B25-F4A3-4A474BC9CA05#create-and-install-a-custom-app'
                rel='noreferrer'
              >
                {' '}
                Guide ↗
              </a>
            </p>
            <p>
              2. Copy the Store Front API token.
              <br />
              <a
                className={styles.guide}
                rel='noreferrer'
                target='_blank'
                href='https://help.shopify.com/en/manual/apps/app-types/custom-apps?shpxid=ddb55a21-CBD3-4B25-F4A3-4A474BC9CA05#get-the-api-credentials-for-a-custom-app'
              >
                {' '}
                Guide ↗
              </a>
            </p>
            <p>
              3. Provide access scope to read the products in store front access
              API settings.
              <br />
              <a
                className={styles.guide}
                rel='noreferrer'
                target='_blank'
                href='https://help.shopify.com/en/manual/apps/app-types/custom-apps?shpxid=ddb55a21-CBD3-4B25-F4A3-4A474BC9CA05#update-storefront-api-access-scopes'
              >
                {' '}
                Guide ↗
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ShopifySettings;
